import { Button, Input } from '@root/components'
import { TranslatedInput } from '@root/components/TranslatedInput/TranslatedInput'
import { SOFT_REMINDER_MAX_DAYS, SOFT_REMINDER_MIN_DAYS, SUPPORTED_LANGUAGES } from '@constants'
import { useBusinessContext } from '@root/context'
import { SoftReminderStep } from '@root/query'
import { businessDefaultInvoicingLanguage } from '@root/utils'
import React, { useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaEnvelope, FaEye, FaTimes } from 'react-icons/fa'
import { ReminderEmailPreviewModal } from './ReminderEmailPreviewModal'
import {
  Eyebrow,
  MessagePreviewBlock,
  PreviewRow,
  ReminderCard,
  ReminderCardHeader,
  ReminderEmailNote,
  SubjectBlock,
  SubjectLabel,
  SubjectValue
} from './PaymentRemindersAndCollectionForm.styled'
import { PaymentRemindersFormValues } from './paymentRemindersFormValues'

interface Props {
  index: number
  onRemove: () => void
  daysError?: string
}

export const ReminderStepCard: React.FC<Props> = ({ index, onRemove, daysError }) => {
  const { t } = useTranslation()
  const { data: business } = useBusinessContext()
  const { control, register } = useFormContext<PaymentRemindersFormValues>()
  const defaultLanguage = businessDefaultInvoicingLanguage(business)
  const [messageLang, setMessageLang] = useState<string | undefined>(defaultLanguage)
  const [previewOpen, setPreviewOpen] = useState(false)
  const subjectLang = messageLang || defaultLanguage
  const stepMessage = useWatch({
    control,
    name: `soft_reminder_steps.${index}.message`
  })

  const emailSubject = useMemo(() => {
    const invoiceNumberPlaceholder = t('settings.invoicing.softReminderInvoiceNumberPlaceholder', {
      lng: subjectLang
    })
    return t('settings.invoicing.softReminderEmailSubject', {
      lng: subjectLang,
      businessName: business?.name || '—',
      invoiceNumber: invoiceNumberPlaceholder
    })
  }, [business?.name, subjectLang, t])

  const previewMessage =
    (stepMessage || []).find(field => field.key === subjectLang)?.value?.trim() || ''

  const previewLanguage =
    SUPPORTED_LANGUAGES.find(language => language.value === subjectLang)?.labelKey ?? subjectLang

  return (
    <ReminderCard>
      <ReminderCardHeader>
        <Eyebrow $tone="info">
          <FaEnvelope />
          {t('settings.invoicing.reminderByEmail')}
        </Eyebrow>
        <Button
          type="button"
          size="sm"
          isSecondary
          icon={<FaTimes />}
          onClick={onRemove}
          style={{ marginLeft: 'auto' }}
        >
          {t('settings.invoicing.softReminderRemoveStep')}
        </Button>
      </ReminderCardHeader>
      <Input
        type="number"
        label={t('settings.invoicing.softReminderDays')}
        info={t('settings.invoicing.softReminderDaysInfo')}
        min={SOFT_REMINDER_MIN_DAYS}
        max={SOFT_REMINDER_MAX_DAYS}
        errors={daysError ? { message: daysError } : undefined}
        {...register(`soft_reminder_steps.${index}.days_after_due`, {
          valueAsNumber: true,
          min: {
            value: SOFT_REMINDER_MIN_DAYS,
            message: t('settings.invoicing.softReminderDaysRange') as string
          },
          max: {
            value: SOFT_REMINDER_MAX_DAYS,
            message: t('settings.invoicing.softReminderDaysRange') as string
          },
          validate: (value, formValues) => {
            const days = Number(value)
            const steps = formValues.soft_reminder_steps || []
            if (index > 0) {
              const previous = Number(steps[index - 1]?.days_after_due)
              if (Number.isFinite(previous) && days <= previous) {
                return t('settings.invoicing.softReminderDaysIncreasing') as string
              }
            }
            const next = Number(steps[index + 1]?.days_after_due)
            if (Number.isFinite(next) && days >= next) {
              return t('settings.invoicing.softReminderDaysIncreasing') as string
            }
            return true
          }
        })}
      />
      <ReminderEmailNote>{t('settings.invoicing.softReminderInvoiceAttached')}</ReminderEmailNote>
      <SubjectBlock>
        <SubjectLabel>{t('settings.invoicing.softReminderEmailSubjectLabel')}</SubjectLabel>
        <SubjectValue>{emailSubject}</SubjectValue>
      </SubjectBlock>
      <MessagePreviewBlock>
        <Controller
          control={control}
          name={`soft_reminder_steps.${index}.message`}
          render={({ field }) => (
            <TranslatedInput
              name={field.name}
              value={(field.value || []) as SoftReminderStep['message']}
              onChange={field.onChange}
              prioritizedLang={defaultLanguage}
              multiline
              languageSelector="pills"
              languageSelectorInfo={t('settings.invoicing.softReminderLanguageInfo')}
              label={t('settings.invoicing.softReminderMessage')}
              info={t('settings.invoicing.softReminderMessageInfo')}
              onActiveLanguageChange={setMessageLang}
            />
          )}
        />
        <PreviewRow>
          <Button
            type="button"
            isSecondary
            size="sm"
            icon={<FaEye />}
            onClick={() => setPreviewOpen(true)}
          >
            {t('settings.invoicing.softReminderPreview')}
          </Button>
        </PreviewRow>
      </MessagePreviewBlock>
      <ReminderEmailPreviewModal
        isVisible={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={t('settings.invoicing.softReminderPreviewTitle', {
          number: index + 1,
          language: t(previewLanguage)
        })}
        language={subjectLang}
        message={previewMessage}
      />
    </ReminderCard>
  )
}
